import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';
import { KRAFT } from '@/constants/testIds';
import { scrollToId } from '@/components/SmoothScroll';

const NAV_ITEMS = [
  { id: 'about', label: 'Bezpieczeństwo', testId: KRAFT.headerNavAbout },
  { id: 'services', label: 'Oferta', testId: KRAFT.headerNavServices },
  { id: 'gallery', label: 'Realizacje', testId: KRAFT.headerNavGallery },
  { id: 'hours', label: 'Godziny', testId: KRAFT.headerNavHours },
  { id: 'booking', label: 'Rezerwacja', testId: KRAFT.headerNavBooking },
  { id: 'contact', label: 'Kontakt', testId: KRAFT.headerNavContact },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const jump = (id) => {
    setMobileOpen(false);
    setTimeout(() => scrollToId(id), 50);
  };

  return (
    <motion.header
      data-testid={KRAFT.headerRoot}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border,backdrop-filter] duration-500 ${
        scrolled
          ? 'bg-ink-900/85 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between gap-6 xl:gap-10 h-20">
          {/* Logo */}
          <button
            data-testid={KRAFT.headerLogo}
            onClick={() => jump('top')}
            className="group flex items-center gap-3 shrink-0"
          >
            <div className="text-left leading-tight">
              <div className="font-display text-lg tracking-tight uppercase text-white">
                Kraft<span className="text-volt">·</span>Performance
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">
                Elektryk · Warszawa
              </div>
            </div>
          </button>

          {/* Nav (desktop) */}
          <nav className="hidden xl:flex items-center gap-7 mx-auto">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={item.testId}
                onClick={() => jump(item.id)}
                className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-white transition-colors whitespace-nowrap"
              >
                {item.label}
                <span className="absolute -bottom-2 left-0 h-px w-0 bg-volt transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          {/* Phone CTAs */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <a
              data-testid={KRAFT.headerPhone1}
              href="tel:+48731842736"
              className="group flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-volt/60 transition-colors whitespace-nowrap"
            >
              <Phone className="w-3.5 h-3.5 text-volt" strokeWidth={2.5} />
              <span className="font-mono text-xs tracking-wider text-white">+48 731 842 736</span>
            </a>
            <a
              data-testid={KRAFT.headerPhone2}
              href="tel:+48886538014"
              className="hidden 2xl:flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-volt/60 transition-colors whitespace-nowrap"
            >
              <Phone className="w-3.5 h-3.5 text-volt" strokeWidth={2.5} />
              <span className="font-mono text-xs tracking-wider text-white">+48 886 538 014</span>
            </a>
            <button
              data-testid={KRAFT.headerCtaEmergency}
              onClick={() => jump('booking')}
              className="btn-volt !py-3 !px-4 !text-[11px] whitespace-nowrap"
            >
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-red-500 animate-ping" />
                <span className="relative rounded-full w-2 h-2 bg-red-500" />
              </span>
              Pogotowie 24/7
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden p-2 border border-white/10 text-white shrink-0"
            aria-label="menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-ink-900/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 space-y-4">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => jump(item.id)}
                  className="block w-full text-left font-display uppercase text-white text-lg tracking-wide"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 space-y-2 border-t border-white/10">
                <a href="tel:+48731842736" className="block font-mono text-sm text-volt">+48 731 842 736</a>
                <a href="tel:+48886538014" className="block font-mono text-sm text-volt">+48 886 538 014</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
